<script setup>
// Dock global de ligações (AstraCall). Fica no App inteiro: banner de chamada
// entrante (atender/recusar) + card da(s) chamada(s) ativa(s) com mute/encerrar.
import { onMounted, onBeforeUnmount, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAstraCalls } from 'dashboard/composables/useAstraCalls';

const { t } = useI18n();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);

const {
  state,
  ensureInit,
  accept,
  reject,
  hangup,
  toggleMute,
  toggleHand,
  toggleLocalVideo,
  acceptVideo,
  rejectVideo,
  remoteVideoStreamFor,
  localVideoStreamFor,
  videoSupported,
  activeCalls,
  incomingCalls,
} = useAstraCalls();

// O clique chamava accept() direto: se falhasse, a rejeição sumia e o botão parecia
// morto. Aqui o atendente pelo menos SABE que não deu.
const atender = async c => {
  try {
    await accept(c);
  } catch (e) {
    useAlert(t('CONNECTIONS.call.failed'));
  }
};

// <video> não aceita srcObject via binding — liga imperativamente pelo ref
const bindStream = (el, stream) => {
  if (el && stream && el.srcObject !== stream) el.srcObject = stream;
};

const now = ref(Date.now());
let timer = null;

onMounted(async () => {
  if (accountId.value) await ensureInit(accountId.value);
  timer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});
onBeforeUnmount(() => clearInterval(timer));

const label = c => c.contact_name || c.peer || t('CALLS_CENTER.dock.unknown');
// o call server marca no `incoming` se a chamada já nasceu em vídeo
const emVideo = c => !!c.video && videoSupported;
const initials = c => {
  const n = label(c).trim();
  const p = n.split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '#';
};
const statusText = c => {
  if (c.status === 'ringing')
    return c.direction === 'incoming'
      ? t('CALLS_CENTER.dock.incoming')
      : t('CALLS_CENTER.dock.ringing');
  if (c.status === 'starting') return t('CALLS_CENTER.dock.calling');
  if (c.held) return t('CALLS_CENTER.dock.held');
  if (c.status === 'connected') return t('CALLS_CENTER.dock.inCall');
  return c.status || '';
};
const elapsed = c => {
  if (!c.startedAt || c.status !== 'connected') return '';
  const s = Math.max(
    0,
    Math.round((now.value - new Date(c.startedAt).getTime()) / 1000)
  );
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
</script>

<template>
  <div
    v-if="state.enabled"
    class="fixed z-[9998] flex flex-col gap-3 bottom-4 ltr:right-4 rtl:left-4 w-[20rem] max-w-[calc(100vw-2rem)]"
  >
    <!-- chamadas entrantes -->
    <div
      v-for="c in incomingCalls"
      :key="`in-${c.callId}`"
      class="p-4 rounded-2xl shadow-xl bg-n-solid-1 border border-n-strong"
    >
      <div class="flex items-center gap-3">
        <span class="relative flex size-2.5 shrink-0">
          <span
            class="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-n-teal-9"
          />
          <span
            class="relative inline-flex rounded-full size-2.5 bg-n-teal-9"
          />
        </span>
        <div class="flex-1 min-w-0">
          <p class="flex items-center gap-1 text-xs text-n-slate-11">
            <span v-if="emVideo(c)" class="i-lucide-video size-3.5 shrink-0" />
            {{
              emVideo(c)
                ? t('CALLS_CENTER.dock.incomingVideo')
                : t('CALLS_CENTER.dock.incoming')
            }}
          </p>
          <p class="font-medium truncate text-n-slate-12">{{ label(c) }}</p>
        </div>
      </div>
      <div class="flex gap-2 mt-3">
        <button
          class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-teal-9 hover:brightness-110"
          @click="atender(c)"
        >
          <span
            class="size-4"
            :class="emVideo(c) ? 'i-lucide-video' : 'i-lucide-phone'"
          />
          {{ t('CALLS_CENTER.dock.answer') }}
        </button>
        <button
          class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-ruby-9 hover:brightness-110"
          @click="reject(c)"
        >
          <span class="i-lucide-phone-off size-4" />
          {{ t('CALLS_CENTER.dock.decline') }}
        </button>
      </div>
      <!-- chamada de vídeo: dá para entrar sem abrir a câmera -->
      <button
        v-if="emVideo(c)"
        class="w-full mt-2 text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="accept(c, { video: false })"
      >
        {{ t('CALLS_CENTER.dock.answerAudioOnly') }}
      </button>
    </div>

    <!-- chamadas ativas -->
    <div
      v-for="c in activeCalls"
      :key="`act-${c.callId}`"
      class="p-3 rounded-2xl shadow-xl bg-n-solid-1 border border-n-strong"
    >
      <div class="flex items-center gap-3">
        <div
          class="grid text-sm font-semibold rounded-full size-10 shrink-0 place-items-center bg-n-alpha-2 text-n-slate-11"
        >
          {{ initials(c) }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-medium truncate text-n-slate-12">{{ label(c) }}</p>
          <p class="text-xs truncate text-n-slate-11">
            {{ statusText(c) }}
            <span v-if="elapsed(c)" class="tabular-nums">
              · {{ elapsed(c) }}</span
            >
          </p>
          <p
            v-if="c.group"
            class="text-[11px] truncate text-n-slate-10"
          >
            <span class="i-lucide-users size-3 align-middle" />
            {{ (c.participants && c.participants.length) || c.targets || '' }}
            participantes
          </p>
        </div>
      </div>
      <!-- vídeo: remoto grande + preview local (PiP). Só aparece quando há vídeo. -->
      <div
        v-if="c.remoteVideo || c.localVideo"
        class="relative mt-3 overflow-hidden bg-black rounded-xl aspect-video"
      >
        <video
          v-if="c.remoteVideo"
          :ref="el => bindStream(el, remoteVideoStreamFor(c.callId))"
          autoplay
          playsinline
          class="object-cover w-full h-full"
        />
        <div
          v-else
          class="grid w-full h-full text-xs place-items-center text-n-slate-9"
        >
          {{ t('CALLS_CENTER.dock.videoWaiting') }}
        </div>
        <video
          v-if="c.localVideo"
          :ref="el => bindStream(el, localVideoStreamFor(c.callId))"
          autoplay
          playsinline
          muted
          class="absolute object-cover w-16 rounded-lg shadow-lg bottom-2 ltr:right-2 rtl:left-2 aspect-video border border-white/30"
        />
      </div>

      <!-- pedido de vídeo do peer -->
      <div
        v-if="c.upgradeIncoming"
        class="flex items-center gap-2 p-2 mt-3 text-xs rounded-lg bg-n-alpha-2"
      >
        <span class="flex-1 text-n-slate-11">
          {{ t('CALLS_CENTER.dock.videoRequest') }}
        </span>
        <button
          class="px-2 py-1 font-medium text-white rounded-md bg-n-teal-9 hover:brightness-110"
          @click="acceptVideo(c)"
        >
          {{ t('CALLS_CENTER.dock.videoAccept') }}
        </button>
        <button
          class="px-2 py-1 font-medium rounded-md bg-n-alpha-3 text-n-slate-11"
          @click="rejectVideo(c)"
        >
          {{ t('CALLS_CENTER.dock.videoReject') }}
        </button>
      </div>

      <div class="flex gap-2 mt-3">
        <button
          class="inline-flex items-center justify-center rounded-lg size-9 shrink-0"
          :class="
            c.muted
              ? 'bg-n-amber-3 text-n-amber-11'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          :title="
            c.muted
              ? t('CALLS_CENTER.dock.unmute')
              : t('CALLS_CENTER.dock.mute')
          "
          @click="toggleMute(c)"
        >
          <span
            :class="c.muted ? 'i-lucide-mic-off' : 'i-lucide-mic'"
            class="size-4"
          />
        </button>
        <button
          class="inline-flex items-center justify-center rounded-lg size-9 shrink-0"
          :class="
            c.handRaised
              ? 'bg-n-amber-3 text-n-amber-11'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          :title="c.handRaised ? 'Baixar a mão' : 'Levantar a mão'"
          @click="toggleHand(c)"
        >
          <span class="i-lucide-hand size-4" />
        </button>
        <button
          v-if="videoSupported && !c.group"
          class="inline-flex items-center justify-center rounded-lg size-9 shrink-0"
          :class="
            c.localVideo
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          :title="
            c.localVideo
              ? t('CALLS_CENTER.dock.cameraOff')
              : t('CALLS_CENTER.dock.cameraOn')
          "
          @click="toggleLocalVideo(c)"
        >
          <span
            :class="c.localVideo ? 'i-lucide-video' : 'i-lucide-video-off'"
            class="size-4"
          />
        </button>
        <button
          class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-ruby-9 hover:brightness-110"
          @click="hangup(c)"
        >
          <span class="i-lucide-phone-off size-4" />
          {{ t('CALLS_CENTER.dock.hangup') }}
        </button>
      </div>
    </div>
  </div>
</template>
